import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Newsletter from "@/components/Newsletter";
import { BLOG_ARTICLES } from "@/data/blog";
function Block({ block }) {
  if (block.type === "h2")
    return <h2 className="mt-12 font-heading text-2xl font-medium text-[#4B2E5A] md:text-3xl">{block.text}</h2>;
  if (block.type === "quote")
    return (
      <blockquote className="my-10 border-l-4 border-[#F28C82] pl-6 font-heading text-xl font-light italic text-[#4B2E5A] md:text-2xl">
        {block.text}
      </blockquote>
    );
  if (block.type === "list")
    return (
      <ul className="mt-5 space-y-3">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-lg leading-relaxed text-[#4B2E5A]/75">
            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#F28C82]" />
            {it}
          </li>
        ))}
      </ul>
    );
  return <p className="mt-5 text-lg leading-relaxed text-[#4B2E5A]/75">{block.text}</p>;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_ARTICLES.find((p) => p.slug === slug);
  React.useEffect(() => {
    if (post) document.title = `${post.title} — Naari Sutra`;
    // External (standalone HTML) articles live under /blog/*.html — send the visitor there.
    if (post?.href) window.location.replace(post.href);
  }, [post]);
  if (!post) return <Navigate to="/blog" replace />;
  if (post.href) return null; // redirecting to the static article
  const others = BLOG_ARTICLES.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div data-testid="blog-post-page" className="pt-32">
      <article className="relative">
        <section className="relative overflow-hidden py-12 md:py-16">
          <div className="mesh-bg">
            <div className="mesh-blob" style={{ width: 400, height: 400, background: "#F5C86B", top: -100, right: -60 }} />
          </div>
          <div className="relative z-10 mx-auto max-w-3xl px-6 md:px-12">
            <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-1.5 text-sm text-[#4B2E5A]/60 hover:text-[#4B2E5A]">
              <ArrowLeft className="h-4 w-4" /> All insights
            </Link>
            <Reveal className="mt-8">
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="rounded-full bg-[#B8E3D9]/40 px-3 py-1 font-medium text-[#4B2E5A]">{post.category}</span>
                <span className="text-[#4B2E5A]/50">{post.date}</span>
                <span className="text-[#4B2E5A]/50">· {post.read}</span>
                <span className="text-[#4B2E5A]/50">· By {post.author}</span>
              </div>
              <h1 className="mt-5 font-heading text-4xl font-light leading-tight tracking-tighter text-[#4B2E5A] md:text-5xl">
                {post.title}
              </h1>
              <p className="mt-5 text-lg leading-relaxed text-[#4B2E5A]/60">{post.excerpt}</p>
            </Reveal>
          </div>
        </section>

        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <Reveal>
            <div className="overflow-hidden rounded-[24px] shadow-[0_20px_60px_rgba(75,46,90,0.12)]">
              <img src={post.image} alt={post.title} className="h-64 w-full object-cover md:h-96" />
            </div>
          </Reveal>

          <div className="pb-12 pt-4">
            {post.content.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>

          <div className="mb-20 border-t border-[#4B2E5A]/8 pt-10">
            <h3 className="font-heading text-xl font-medium text-[#4B2E5A]">Keep reading</h3>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              {others.map((o) => {
                const relClass = "glass group rounded-[24px] p-6 transition-transform hover:-translate-y-1";
                const relInner = (
                  <>
                    <span className="rounded-full bg-[#B8E3D9]/40 px-3 py-1 text-xs font-medium text-[#4B2E5A]">{o.category}</span>
                    <h4 className="mt-4 font-heading text-lg font-medium leading-snug text-[#4B2E5A]">{o.title}</h4>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#F28C82]">
                      Read <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </>
                );
                return o.href ? (
                  <a key={o.slug} href={o.href} data-testid={`blog-related-${o.slug}`} className={relClass}>{relInner}</a>
                ) : (
                  <Link key={o.slug} to={`/blog/${o.slug}`} data-testid={`blog-related-${o.slug}`} className={relClass}>{relInner}</Link>
                );
              })}
            </div>
          </div>
        </div>
      </article>
      <Newsletter />
    </div>
  );
}
